import { makeStyles } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

const fadeSlideIn = {
  from: {
    opacity: 0,
    transform: "translateY(6px)",
  },
  to: {
    opacity: 1,
    transform: "translateY(0)",
  },
};

const shimmer = {
  "0%": {
    backgroundPosition: "-1000px 0",
  },
  "100%": {
    backgroundPosition: "1000px 0",
  },
};

export const useStyles = makeStyles({
  container: {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    paddingRight: designTokens.spacing[10],
    margin: `${designTokens.spacing[1]} 0`,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.lg,
    cursor: "pointer",
    backgroundColor: designTokens.neutral.white,
    transition: `all ${designTokens.transition.fast}`,
    gap: designTokens.spacing[3],
    animationName: fadeSlideIn,
    animationDuration: designTokens.transition.base,
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationName: "none",
      transitionDuration: "0ms",
    },
    ":hover": {
      backgroundColor: designTokens.accent[50],
      border: `1px solid ${designTokens.accent[500]}`,
    },
    "@media (max-width: 640px)": {
      padding: `${designTokens.spacing[3]} ${designTokens.spacing[3]}`,
      paddingRight: designTokens.spacing[10],
      minHeight: "56px",
      gap: designTokens.spacing[2],
    },
    "@media (max-width: 375px)": {
      padding: `${designTokens.spacing[2]} ${designTokens.spacing[2]}`,
      paddingRight: designTokens.spacing[8],
    },
  },
  containerSelected: {
    backgroundColor: designTokens.accent[50],
    border: `1px solid ${designTokens.accent[500]}`,
  },
  containerLoading: {
    opacity: 0.7,
    pointerEvents: "none",
    "&::before": {
      content: "''",
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.08) 50%, transparent 100%)`,
      backgroundSize: "1000px 100%",
      animationName: shimmer,
      animationDuration: "1s",
      animationIterationCount: "infinite",
      animationTimingFunction: "ease-in-out",
      pointerEvents: "none",
      borderRadius: designTokens.borderRadius.lg,
      "@media (prefers-reduced-motion: reduce)": {
        animationName: "none",
      },
    },
  },
  iconArea: {
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    width: designTokens.spacing[6],
    height: designTokens.spacing[6],
    minWidth: designTokens.spacing[6],
  },
  icon: {
    color: designTokens.neutral[500],
    width: "20px",
    height: "20px",
  },
  contentArea: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    gap: designTokens.spacing[1],
  },
  title: {
    display: "flex",
    alignItems: "center",
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.bold,
    color: designTokens.neutral[900],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  titleText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  favoritedIcon: {
    flexShrink: 0,
    fontSize: designTokens.fontSize.base,
    color: designTokens.accent[600],
    marginRight: designTokens.spacing[1],
  },
  metadataRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: designTokens.spacing[3],
    rowGap: designTokens.spacing[1],
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
  },
  metadataItem: {
    display: "flex",
    alignItems: "center",
  },
  workspaceItemAsItalic: {
    fontStyle: "italic",
  },
  flexRowForWorkspaces: {
    display: "flex",
    gap: designTokens.spacing[1],
  },
  metadataItemMobile: {
    display: "flex",
    alignItems: "center",
    fontStyle: "italic",
  },
  metadataItemHideMobile: {
    display: "flex",
    alignItems: "center",
    "@media (max-width: 640px)": {
      display: "none",
    },
  },
  menuArea: {
    position: "absolute",
    top: "50%",
    right: designTokens.spacing[3],
    transform: "translateY(-50%)",
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: `opacity ${designTokens.transition.fast}`,
    cursor: "default",
    ":focus-within": {
      opacity: 1,
    },
    "@media (max-width: 640px)": {
      opacity: 1,
      width: "44px",
      height: "44px",
    },
  },
  menuAreaVisible: {
    opacity: 1,
  },
  highlightedText: {
    color: designTokens.accent[700],
    fontWeight: designTokens.fontWeight.bold,
    backgroundColor: designTokens.accent[50],
  },
});
